import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Box,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
  Snackbar,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import PaymentsIcon from '@mui/icons-material/Payments';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';

const CashTipDetailsDialog = ({
  open,
  onClose,
  businessDate,
  storeId,
  memoDetail,
  firebaseUser,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [details, setDetails] = useState([]);
  const [showCashGuid, setShowCashGuid] = useState(false);
  const [orderBy, setOrderBy] = useState('tipAmount');
  const [order, setOrder] = useState('desc');
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const [columnFilters, setColumnFilters] = useState({});

  const handleCopy = () => {
    setCopySnackbarOpen(true);
  };

  const handleColumnFilterChange = (columnId, value) => {
    setColumnFilters((prev) => ({
      ...prev,
      [columnId]: value,
    }));
  };

  const clearColumnFilter = (columnId) => {
    setColumnFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[columnId];
      return newFilters;
    });
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filteredDetails = useMemo(() => {
    if (!details.length) return details;
    const activeFilters = Object.entries(columnFilters).filter(([_, value]) => value.trim() !== '');
    if (activeFilters.length === 0) return details;
    return details.filter((row) => {
      return activeFilters.every(([columnId, filterValue]) => {
        const cellValue = String(row[columnId] ?? '').toLowerCase();
        const filterLower = filterValue.toLowerCase();
        return cellValue.includes(filterLower);
      });
    });
  }, [details, columnFilters]);

  const sortedDetails = useMemo(() => {
    if (!filteredDetails.length) return filteredDetails;

    return [...filteredDetails].sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];

      // Handle numeric sorting for tip amount
      if (orderBy === 'tipAmount') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else {
        // String sorting
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }

      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredDetails, orderBy, order]);

  useEffect(() => {
    if (open && businessDate && storeId) {
      fetchDetails();
    }
  }, [open, businessDate, storeId]);

  const fetchDetails = async () => {
    if (!firebaseUser) return;

    try {
      setLoading(true);
      setError(null);

      const firebaseUserIdToken = await firebaseUser.getIdToken(true);

      const response = await axios.get('/hd-api/gl-export/cash-tip-details', {
        params: {
          businessDate,
          storeId,
        },
        headers: {
          Authorization: `Bearer ${firebaseUserIdToken}`,
        },
      });

      setDetails(response.data.data || []);
    } catch (err) {
      console.error('Error fetching cash tip details:', err);
      setError('Failed to load cash tip details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '-';
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return '-';
    }
  };

  // Calculate total
  const total = useMemo(() => {
    return filteredDetails.reduce((acc, d) => acc + (parseFloat(d.tipAmount) || 0), 0);
  }, [filteredDetails]);

  const hasActiveFilters = Object.values(columnFilters).some((v) => v.trim() !== '');

  // Export to Excel
  const handleExport = () => {
    const exportData = sortedDetails.map((item) => ({
      'Cash GUID': item.cashGuid,
      'Tip Amount': parseFloat(item.tipAmount) || 0,
      'Entry Time': item.entryTime,
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Cash Tips');
    XLSX.writeFile(wb, `cash-tips-${storeId}-${businessDate}.xlsx`);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '80vh' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PaymentsIcon color="success" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" component="span">
            Cash Tips Paid In
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
            Store {storeId} - {businessDate}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && details.length === 0 && (
          <Alert severity="info">
            No cash tip entries found for this date.
          </Alert>
        )}

        {!loading && !error && details.length > 0 && (
          <>
            <TableContainer sx={{ maxHeight: 400, userSelect: 'text' }} onCopy={handleCopy}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {showCashGuid && (
                      <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                        <TableSortLabel
                          active={orderBy === 'cashGuid'}
                          direction={orderBy === 'cashGuid' ? order : 'asc'}
                          onClick={() => handleRequestSort('cashGuid')}
                        >
                          Cash GUID
                        </TableSortLabel>
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }} align="right">
                      <TableSortLabel
                        active={orderBy === 'tipAmount'}
                        direction={orderBy === 'tipAmount' ? order : 'asc'}
                        onClick={() => handleRequestSort('tipAmount')}
                      >
                        Amount
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                      <TableSortLabel
                        active={orderBy === 'entryTime'}
                        direction={orderBy === 'entryTime' ? order : 'asc'}
                        onClick={() => handleRequestSort('entryTime')}
                      >
                        Entry Time
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    {showCashGuid && (
                      <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                        <TextField
                          size="small"
                          placeholder="Filter..."
                          value={columnFilters.cashGuid || ''}
                          onChange={(e) => handleColumnFilterChange('cashGuid', e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <FilterListIcon fontSize="small" />
                              </InputAdornment>
                            ),
                            endAdornment: columnFilters.cashGuid && (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  onClick={() => clearColumnFilter('cashGuid')}
                                  edge="end"
                                >
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          sx={{ width: '100%' }}
                        />
                      </TableCell>
                    )}
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }} align="right">
                      <TextField
                        size="small"
                        placeholder="Filter..."
                        value={columnFilters.tipAmount || ''}
                        onChange={(e) => handleColumnFilterChange('tipAmount', e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters.tipAmount && (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => clearColumnFilter('tipAmount')}
                                edge="end"
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{ width: '100%' }}
                      />
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                      <TextField
                        size="small"
                        placeholder="Filter..."
                        value={columnFilters.entryTime || ''}
                        onChange={(e) => handleColumnFilterChange('entryTime', e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters.entryTime && (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => clearColumnFilter('entryTime')}
                                edge="end"
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{ width: '100%' }}
                      />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedDetails.map((detail, index) => (
                    <TableRow
                      key={`${detail.cashGuid}-${index}`}
                      sx={{
                        '&:hover': { backgroundColor: '#f5f5f5' },
                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafafa',
                      }}
                    >
                      {showCashGuid && (
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {detail.cashGuid || '-'}
                        </TableCell>
                      )}
                      <TableCell align="right" sx={{ color: 'success.main', fontWeight: 500 }}>
                        {formatCurrency(detail.tipAmount)}
                      </TableCell>
                      <TableCell>
                        {formatTime(detail.entryTime)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Summary */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 2,
                pt: 2,
                borderTop: '1px solid #e0e0e0',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {filteredDetails.length} entr{filteredDetails.length !== 1 ? 'ies' : 'y'}
                  {hasActiveFilters && ` (filtered from ${details.length})`}
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={showCashGuid}
                      onChange={(e) => setShowCashGuid(e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">Show Cash GUID</Typography>}
                />
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body2" color="text.secondary">
                  Total
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main' }}>
                  {formatCurrency(total)}
                </Typography>
              </Box>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleExport}
          disabled={details.length === 0}
        >
          Export
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={2000}
        onClose={() => setCopySnackbarOpen(false)}
        message="Copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Dialog>
  );
};

export default CashTipDetailsDialog;
